import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import markdownItAttrs from 'markdown-it-attrs';
import api from '../api/api';
import ManualBarGraph from '../graph/ManualBarGraph';
import ManualLineGraph from '../graph/ManualLineGraph';
import ManualLineBarGraph from '../graph/ManualLineBarGraph';
import ManualMarketLineGraph from '../graph/ManualMarketLineGraph';

const mdParser = new MarkdownIt();
mdParser.use(markdownItAttrs);
mdParser.set({ html: true });

export const numberOutline = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  const numbering = [0, 0, 0];
  const result = [];
  lines.forEach((line) => {
    if (line.startsWith('# ')) {
      result.push(line);
    } else if (line.startsWith('## ')) {
      numbering[0]++;
      numbering[1] = 0;
      numbering[2] = 0;
      result.push(`## ${numbering[0]}. ${line.substring(3)}`);
    } else if (line.startsWith('### ')) {
      numbering[1]++;
      numbering[2] = 0;
      result.push(`### ${numbering[0]}-${numbering[1]}. ${line.substring(4)}`);
    } else if (line.startsWith('#### ')) {
      numbering[2]++;
      result.push(`#### ${numbering[0]}-${numbering[1]}-${numbering[2]}. ${line.substring(5)}`);
    } else {
      result.push(line);
    }
  });
  return result.join('\n');
};

export const modifyData = (data) => {
  return data.map((dt) => {
    if (dt.x_unit === "月期" && Array.isArray(dt.x)) {
      return {
        ...dt,
        x: dt.x.map((dateStr) => {
          const date = new Date(dateStr);
          return `${date.getFullYear()}/${date.getMonth() + 1}`;
        })
      };
    }
    return dt;
  });
};

export const getFigType = (fig_data) => {
  const graphTypes = fig_data.map((fig) => fig.graph_type);
  if (graphTypes.includes("bar") && graphTypes.includes("line")) {
    return "line-bar";
  } else if (graphTypes.includes("bar")) {
    return "bar";
  } else if (graphTypes.includes("line")) {
    return "line";
  } else {
    return graphTypes[0];
  }
};

export const renderMarkdownWithGraphs = (text, figures, modifyDataFn) => {
  if (!text) return [];
  const parts = text.split(/(@@GraphPlaceholder-\d+@@)/g);
  const result = [];
  let placeholderBuffer = [];
  let currentGraphGroupSize = 0;
  
  parts.forEach((part, index) => {
    const match = part.match(/@@GraphPlaceholder-(\d+)@@/);
    if (match) {
      const figIndex = parseInt(match[1], 10);
      const figData = figures[figIndex]?.fig_data || [];
      const figType = getFigType(figData);
      let figWidth = 400;
      const isNextGraph = parts[index + 1] && parts[index + 1].match(/@@GraphPlaceholder-\d+@@/);
      currentGraphGroupSize = currentGraphGroupSize === 0 ? 1 : currentGraphGroupSize + 1;
      if (!isNextGraph) {
        figWidth = currentGraphGroupSize === 1 ? 500 : 400;
      }
      let element = null;
      if (figType === "bar") {
        element = (
          <ManualBarGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={350}
          />
        );
      } else if (figType === "line") {
        element = (
          <ManualLineGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={350}
          />
        );
      } else if (figType === "line-bar") {
        element = (
          <ManualLineBarGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
          />
        );
      } else if (figType === "market-line") {
        element = (
          <ManualMarketLineGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={350}
          />
        );
      }
      if (element) {
        placeholderBuffer.push(element);
      }
      if (!isNextGraph) {
        currentGraphGroupSize = 0;
      }
    } else {
      if (part.trim() === "") return;
      if (placeholderBuffer.length > 0) {
        result.push(
          <div key={`group-${index}`} className="flex flex-wrap gap-[1rem]">
            {placeholderBuffer}
          </div>
        );
        placeholderBuffer = [];
      }
      result.push(
        <span
          key={`text-${index}`}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(mdParser.render(part))
          }}
        />
      );
    }
  });
  if (placeholderBuffer.length > 0) {
    result.push(
      <div key={`group-end`} className="flex wrap gap-[1rem]">
        {placeholderBuffer}
      </div>
    );
  }
  return result;
};

export const getGraphData = async (settings) => {
  try {
    const response = await api.post(`${process.env.REACT_APP_API_URL}/chat/api/report-graph/`, { settings });
    return response.data.fig_data;
  } catch (error) {
    console.error("APIエラー:", error);
    return null;
  }
};

export const replaceGraph = async (contents, setProcessedContent, renderMarkdownWithGraphsFn, figures, modifyDataFn) => {
  let updatedText = contents;
  const newFigures = [];
  const regex = /\[type=(.+?)\]/g;
  let match;
  let index = 0;
  while ((match = regex.exec(contents)) !== null) {
    const settings = match[0];
    const graphData = await getGraphData(settings);
    newFigures.push({
      fig_data: graphData,
      replace_string: settings,
    });
    updatedText = updatedText.replace(settings, `@@GraphPlaceholder-${index}@@`);
    index++;
  }
  setProcessedContent(renderMarkdownWithGraphsFn(updatedText, newFigures, modifyDataFn));
};

export const replaceRef = (contents, setReferences) => {
  const refRegex = /\[ref (.*?)\]/g;
  let matches = [];
  let match;
  while ((match = refRegex.exec(contents)) !== null) {
    matches.push(match[1]);
  }
  const uniqueRefs = [...new Set(matches)];
  const refMap = new Map();
  uniqueRefs.forEach((ref, index) => refMap.set(ref, index + 1));
  let updatedContents = contents.replace(refRegex, (match, p1) => {
    return `<a class="text-xs" href="#ref-${refMap.get(p1)}">[${refMap.get(p1)}]</a>`;
  });
  setReferences(uniqueRefs);
  return updatedContents;
};
